import React, { useEffect, useState } from 'react';
import { Button } from './button';
import { X, Maximize2, Filter, Palette } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './tooltip';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "./sheet";
import FilterSection from './filter';
import { useToast } from './use-toast';
import { trackMapConfigEvents } from '../utils/ga4Events';

const StaticImageSlideshow = ({ onSettingsChange, children }) => {
  const [staticImages, setStaticImages] = useState([]);
  const [filteredImages, setFilteredImages] = useState([]);
  const [enlargedImage, setEnlargedImage] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [selectedStyle, setSelectedStyle] = useState(null);
  const [showButtonTooltip, setShowButtonTooltip] = useState(false);
  const { toast } = useToast();
  // Filter states
  const [showTopographyOnly, setShowTopographyOnly] = useState(false);

  useEffect(() => {
    if (selectedImage) {
      setShowButtonTooltip(true);
      const timer = setTimeout(() => {
        setShowButtonTooltip(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [selectedImage]);

  useEffect(() => {
    fetch('/assets/settings.json')
      .then(response => response.json())
      .then(data => {
        setStaticImages(data.templates);
        setFilteredImages(data.templates);
      })
      .catch(error => {
        console.error('Error loading templates:', error);
        toast({
          title: "Error",
          description: "An error occurred while loading templates",
          variant: "destructive",
          duration: 3000,
        });
      });
  }, []);

  useEffect(() => {
    let filtered = staticImages;

    if (showTopographyOnly) {
      filtered = filtered.filter(image => image.settings.topography);
    }

    setFilteredImages(filtered);
  }, [staticImages, showTopographyOnly]);

  const handleImageClick = (image) => {
    setSelectedImage(image.id);
  };

  const handleEnlargeImage = (e, image) => {
    e.stopPropagation();
    setEnlargedImage(image);
  };

  const handleCloseEnlarged = () => {
    setEnlargedImage(null);
  };

  const handleApplySettings = () => {
    const selectedTemplate = staticImages.find(img => img.id === selectedImage);
    if (selectedTemplate) {
      onSettingsChange(selectedTemplate.settings);
      setSelectedImage(null);
      setSelectedStyle(selectedImage);
      trackMapConfigEvents.styleSelected(selectedTemplate.name);
    }
  };

  return (
    <div className="relative space-y-4">
      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-3 gap-2 lg:gap-4 w-full max-h-[calc(1.8*10rem)] md:max-h-[calc(3*10rem)] lg:max-h-[calc(4*10rem)] overflow-y-auto p-2 mb-4">
        {filteredImages.map((image) => (
          <div
            key={image.id}
            className={`aspect-[4/3] relative group cursor-pointer rounded-xl
              ${selectedImage === image.id ? 'ring-2 ring-blue-500' : ''}
              ${selectedStyle === image.id ? 'ring-2 ring-red-500' : ''}`}
            onClick={() => handleImageClick(image)}
          >
            <img
              src={image.imageUrl}
              alt={image.name}
              className="w-full h-full object-cover rounded-lg border border-gray-200 rounded-xl"
            />
            <div className="absolute bottom-0 left-0 right-0 bg-black bg-opacity-50 text-white p-2 rounded-xl 
                        group-hover:bg-opacity-70 transition-colors">
              <div className="flex justify-between items-center">
                <p className="text-xs truncate">{image.name}</p>
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-6 w-6 text-white hover:text-blue-400"
                        onClick={(e) => handleEnlargeImage(e, {
                          name: image.name,
                          imageUrl: image.imageUrl
                        })}
                      >
                        <Maximize2 className="h-3 w-3" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>
                      <p>View larger image</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
          </div>
        ))}
      </div>
      <div className="flex justify-center space-x-2 items-center">
        <div className="w-4 h-4 flex items-center justify-center">
          {selectedImage && (
            <div className="text-gray-700 animate-wiggle">
              ➜
            </div>
          )}
        </div>
        <Button
          size="sm"
          variant="black"
          onClick={handleApplySettings}
          disabled={!selectedImage}
          className={`
            transition-all duration-300 border-0 text-xs lg:text-md
            ${selectedImage ?
              'rounded-3xl' :
              'rounded-3xl bg-gray-100 text-gray-400'
            }
          `}
        >
          Select Style
        </Button>
        <div className="space-x-2 flex">
          {children}
        </div>
      </div>

      {enlargedImage && (
        <div
          className="fixed inset-0 bg-black backdrop-blur-sm bg-opacity-50 flex items-center justify-center z-[9999] p-4"
          onClick={handleCloseEnlarged}
        >
          <div
            className="bg-white rounded-xl w-full max-w-3xl relative"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center p-4 border-b">
              <h3 className="text-lg font-semibold">{enlargedImage.name}</h3>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCloseEnlarged}
                className="text-gray-500 hover:text-gray-700"
              >
                <X className="h-6 w-6" />
              </Button>
            </div>
            <div className="p-6">
              <img
                src={enlargedImage.imageUrl}
                alt={`Enlarged preview for ${enlargedImage.name}`}
                className="w-full h-auto rounded-lg shadow-2xl shadow-black/50"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default StaticImageSlideshow;